import {v4} from 'uuid';
import {useIntl} from 'react-intl';
import React, {useCallback, useMemo, useState} from 'react';

import {ACCEPTED_FILES} from './../const';
import {localTypeToBinary} from './../helpers';
import {ListViewer} from './../DataReview/ListViewer';
import {DropArea} from '../../../base-components/StudioDropArea';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import {BINARY_TYPES} from '../../../types/dataService/DatasetMetadata';
import {StudioContentLayout} from '../../../base-components/StudioContentLayout';
import {getFileTypeFromFilename} from '../../../base-components/StudioDataset/const';
import {FileViewer, ItemType} from '../../../base-components/StudioDataset/FileViewer';

import './UploadBinariesTable.scss';

type SortConfig = {
  key: keyof ItemType;
  direction: 'asc' | 'desc';
};

type DataUploadProps = {
  files: ItemType[];
  setFiles: (files: ItemType[]) => void;
};

export const UploadBinariesTable = ({files, setFiles}: DataUploadProps) => {
  const intl = useIntl();

  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'filename',
    direction: 'asc',
  });

  // TODO: activate pagination and selection
  // const [selections, setSelections] = useState<string[]>([]);
  // const [page, setPage] = useState<number>(0);

  const fileToImage = useCallback((file: File) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = () => {
        const image = new Image();

        image.onload = () => {
          resolve(image);
        };

        image.onerror = () => {
          reject();
        };

        if (typeof fileReader.result === 'string') {
          image.src = fileReader.result;
        } else {
          reject();
        }
      };

      fileReader.onerror = () => {
        reject();
      };

      fileReader.readAsDataURL(file);
    });
  }, []);

  const handleUploadedFiles = useCallback(
    async (_files: File[]) => {
      setIsLoading(true);

      const newUploadedFiles: ItemType[] = await Promise.all(
        _files.map(async file => {
          let image: HTMLImageElement | undefined;

          try {
            image = await fileToImage(file);
          } catch (e) {}

          const type = getFileTypeFromFilename(file.name);

          return {
            file,
            type,
            id: v4(),
            selected: false,
            url: image?.src,
            size: file.size,
            width: image?.width,
            filename: file.name,
            height: image?.height,
            lastEdited: new Date(file.lastModified),
            binaryType: localTypeToBinary(type) as BINARY_TYPES,
          };
        })
      );

      setIsLoading(false);
      setFiles([...files, ...newUploadedFiles]);
    },
    [files, fileToImage, setFiles]
  );

  const onSort = useCallback((colId, dir) => {
    setSortConfig({key: colId, direction: dir});
  }, []);

  const sortedFiles = useMemo(() => {
    let finalFiles = files;

    if (sortConfig) {
      finalFiles = [...files].sort((fileA: ItemType, fileB: ItemType) => {
        const valA = fileA[sortConfig.key];
        const valB = fileB[sortConfig.key];

        const multiplier = sortConfig.direction === 'asc' ? 1 : -1;

        return multiplier * (valA < valB ? -1 : valA > valB ? 1 : 0);
      });
    }

    return finalFiles;
  }, [files, sortConfig]);

  return (
    <div className="upload-binaries-table">
      {isLoading ? (
        <div className="upload-binaries-table__loading">
          <Waiting
            diameter={100}
            loadingText={intl.formatMessage({
              id: 'datasetCreation.uploadBinaries.loading',
            })}
          />
        </div>
      ) : (
        <DropArea
          accept={ACCEPTED_FILES}
          className="data-upload__drop"
          onChange={handleUploadedFiles}
          size={files.length ? 'xs' : 'xlg'}
        />
      )}

      {!!sortedFiles.length && (
        <div className="upload-binaries-table__list">
          <StudioContentLayout>
            {() => (
              <FileViewer
                page={0}
                itemList={sortedFiles}
                itemGroup={[
                  {
                    list: sortedFiles,
                    // TODO: change this later when dealing with View responsiveness
                    totalElements: sortedFiles.length / 50,
                  },
                ]}
                view="list"
                onSort={onSort}
                listViewRender={({...args}) => (
                  <ListViewer
                    enableSelection={false}
                    onDelete={itemId => {
                      setFiles(sortedFiles.filter(item => item.id !== itemId));
                    }}
                    {...args}
                    columns={[...args.columns, {id: 'delete', label: '', width: 60}]}
                  />
                )}
                gridViewRender={null}
                labelViewRender={null}
              />
            )}
          </StudioContentLayout>
        </div>
      )}
    </div>
  );
};
